<template>
  <b-container>
    <b-card class="mb-4">
      <b-row align-v="center">
        <b-col cols="12" md="6" lg="4">
          <label class="mb-2 font-weight-bold">Select Date Range to View Reports</label>
          <DatePicker 
            v-model="dateRange" 
            range 
            format="YYYY-MM-DD" 
            placeholder="Select date range"
            @change="handleDateRangeChange" 
            size="sm" 
            style="width: 100%;"
          />
        </b-col>
        <b-col cols="12" md="6" lg="4" class="mt-3 mt-md-0">
          <b-button 
            variant="primary" 
            :disabled="!dateRange" 
            @click="handleDateRangeChange"
            class="ml-md-2"
          >
            Refresh Reports
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <div class="d-flex justify-content-center mb-4" v-if="loading">
      <b-spinner variant="success" label="Loading reports..." style="width: 3rem; height: 3rem"></b-spinner>
      <p class="ml-3 mb-0 align-self-center">Loading reports...</p>
    </div>

    <b-alert show variant="info" v-if="!dateRange && !loading">
      Please select a date range above to view the reports.
    </b-alert>

    <b-row v-if="dateRange && !loading">
      <b-col cols="12" class="mb-4">
        <b-card>
          <div v-if="costAnalysisData && costAnalysisData.length">
            <Chart :costData="costAnalysisData" />
          </div>
          <div v-else class="text-center py-5">
            <i class="fas fa-chart-line text-muted mb-3" style="font-size: 3rem;"></i>
            <h4 class="text-muted">No Cost Analysis Data Available</h4>
            <p>There is no cost analysis data for the selected date range. Try selecting a different period.</p>
          </div>
        </b-card>
      </b-col>
      <b-col cols="12">
        <b-card>
          <div v-if="projectStatusData && projectStatusData.length">
            <DonutChart :statusData="projectStatusData" />
          </div>
          <div v-else class="text-center py-5">
            <i class="fas fa-chart-pie text-muted mb-3" style="font-size: 3rem;"></i>
            <h4 class="text-muted">No Project Status Data Available</h4>
            <p>There is no project status data for the selected date range. Try selecting a different period.</p>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Chart from "./Chart.vue";
import DonutChart from "./DonutChart.vue";
import { BContainer, BRow, BCol, BSpinner, BCard, BButton, BAlert } from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    BSpinner,
    BCard,
    BButton,
    BAlert,
    DonutChart,
    Chart,
    DatePicker
  },

  data() {
    const today = new Date();
    return {
      dateRange: [today, today],
      isInitialLoad: true,
      costAnalysisData: [],
      projectStatusData: []
    };
  },

  computed: {
    ...mapGetters("reportModule", {
      loading: "loading"
    }),
  },

  methods: {
    ...mapActions("reportModule", [
      "getProjectCostAnalysisReportAction",
      "getProjectStatusReportAction",
    ]),

    async handleDateRangeChange() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        return;
      }

      const dateParams = {
        dateFrom: this.formatDate(this.dateRange[0]),
        dateTo: this.formatDate(this.dateRange[1]),
      };

      try {
        await this.fetchAllReports(dateParams);
      } catch (error) {
        console.error("Error updating reports:", error);
      }
    },

    async fetchAllReports(dateParams) {
      try {
        const [costAnalysisResponse, projectStatusResponse] = await Promise.all([
          this.getProjectCostAnalysisReportAction(dateParams),
          this.getProjectStatusReportAction(dateParams),
        ]);

        this.costAnalysisData = costAnalysisResponse || [];
        this.projectStatusData = projectStatusResponse || [];
      } catch (error) {
        console.error('Error fetching project reports:', error);
      }
    },

    formatDate(date) {
      return date.toISOString().split("T")[0]; 
    }
  },

  async mounted() {
    await this.handleDateRangeChange();
  }
};
</script>